import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { en } from './en';
import { th } from './th';
import type { Dictionary, Locale } from './types';

export type { Locale } from './types';

const STORAGE_KEY = 'llmr-member-lang';

/** The stored preference, if the customer has ever chosen one explicitly. */
function storedLocale(): Locale | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'en' || raw === 'th' ? raw : null;
}

/** First-visit default: a Thai browser gets Thai, everything else gets English. */
function browserLocale(): Locale {
  return navigator.language.toLowerCase().startsWith('th') ? 'th' : 'en';
}

const DICTIONARIES: Record<Locale, Dictionary> = { en, th };

interface LanguageState {
  locale: Locale;
  dict: Dictionary;
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageState | null>(null);

/**
 * EN/TH, same pattern as ThemeProvider (src/lib/theme.tsx): defaults from
 * the browser, remembers whatever the customer picks, and applies to
 * `<html lang>` so screen readers and font rendering follow it too.
 *
 * The actual "no flash of the wrong language" behaviour lives in
 * `index.html`'s pre-paint script, which sets `<html lang>` from the same
 * `localStorage` key before React ever mounts — this provider's `useState`
 * initialiser just has to agree with that script so hydration matches what
 * was already painted.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => storedLocale() ?? browserLocale());

  // Kept in sync defensively — the pre-paint script in index.html already
  // sets this before React mounts, using the same localStorage key and the
  // same default logic, so this should be a no-op in practice.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
  }, []);

  const value = useMemo(
    () => ({ locale, dict: DICTIONARIES[locale], setLocale }),
    [locale, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageState {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
